import { useState, useEffect } from 'react';
import api from '../lib/api';
import { CF_REQUEST_TIMEOUT_MS } from '../utils/campaignFinanceUtils';

export interface ContributorTypeOption {
  value: string;
  label: string;
  count: number;
}

/**
 * Data-driven contributor-type options for the Campaign Finance filter.
 * Pulls the distinct (normalized) contributor types from the dedicated
 * GET /campaign-finance/contributor-types endpoint — the same normalization the
 * search filter matches against, so every option is guaranteed to return results.
 */
export function useContributorTypes(): { options: ContributorTypeOption[]; loading: boolean } {
  const [options, setOptions] = useState<ContributorTypeOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/campaign-finance/contributor-types', { timeout: CF_REQUEST_TIMEOUT_MS })
      .then(res => {
        if (cancelled) return;
        const list = res.data?.data || [];
        setOptions(
          list
            .filter((o: { value?: string }) => o.value)
            .map((o: { value: string; label?: string; count?: number | string }) => ({
              value: String(o.value),
              label: String(o.label ?? o.value),
              count: Number(o.count) || 0,
            }))
        );
      })
      .catch(() => { /* non-fatal: the dropdown simply shows only "All Contributors" */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { options, loading };
}
